/**
 * Firebase Authentication のエラーを、画面に出せる日本語メッセージへ変換する。
 *
 * 変換せず `error.message` をそのまま出すと Firebase の英語文言が利用者に見えてしまい、
 * 逆に握り潰すと「ボタンを押しても何も起きない」状態になる。この 2 択を避けるため、
 * すべてのエラーをここで日本語化し、原因が特定できない種類 (configuration / unknown)
 * については文言にエラーコードを残して問い合わせから追跡できるようにする。
 */

export type AuthErrorKind =
  /** 利用者自身がポップアップを閉じた / 連打した。UI には出さない */
  | 'cancelled'
  /** 入力内容の誤り。そのまま利用者に伝えてよい */
  | 'credential'
  /** ブラウザ設定や通信環境に起因する。代替手段を案内する */
  | 'environment'
  /** Firebase / ホスティングの設定不備。利用者では解決できないので問い合わせへ誘導する */
  | 'configuration'
  /** 想定外。コードを添えて汎用文言を出す */
  | 'unknown';

export type AuthErrorDisplay = {
  code: string;
  kind: AuthErrorKind;
  /** 日本語メッセージ。改行は `\n` (`splitParagraph` で <br> に展開される) */
  message: string;
};

const INVALID_CREDENTIAL_MESSAGE = 'メールアドレスまたはパスワードが誤っています';

const SIGN_IN_WITH_EMAIL_GUIDANCE =
  '下のメールアドレスとパスワードでログインしてください。' as const;

// キーは文字列リテラルで書く。firebase/auth の `AuthErrorCodes` は
// INVALID_IDP_RESPONSE と INVALID_LOGIN_CREDENTIALS が同じ 'auth/invalid-credential' を
// 指すため、定数を computed key に使うと重複して片方が消える。
const ERROR_MAP: Record<string, { kind: AuthErrorKind; message: string }> = {
  // kind: 'cancelled' は呼び出し側が表示せず捨てる。
  // ただし将来うっかり表示されても英語が出ないよう、文言自体は日本語で持たせておく。
  'auth/popup-closed-by-user': { kind: 'cancelled', message: 'サインインを中断しました。' },
  'auth/cancelled-popup-request': { kind: 'cancelled', message: 'サインインを中断しました。' },
  'auth/user-cancelled': { kind: 'cancelled', message: 'サインインを中断しました。' },

  'auth/popup-blocked': {
    kind: 'environment',
    message: `ポップアップがブロックされました。\nブラウザの設定でポップアップを許可するか、${SIGN_IN_WITH_EMAIL_GUIDANCE}`,
  },
  'auth/web-storage-unsupported': {
    kind: 'environment',
    message: `ブラウザの設定によりGoogleログインを利用できません。\nCookie (サードパーティCookie) を許可するか、${SIGN_IN_WITH_EMAIL_GUIDANCE}`,
  },
  'auth/network-request-failed': {
    kind: 'environment',
    message:
      'ネットワークに接続できませんでした。\n通信環境をご確認のうえ、もう一度お試しください。',
  },
  'auth/timeout': {
    kind: 'environment',
    message: '時間内に処理が完了しませんでした。\nもう一度お試しください。',
  },
  'auth/too-many-requests': {
    kind: 'environment',
    message: '試行回数が上限に達しました。\nしばらく時間をおいてから、もう一度お試しください。',
  },

  'auth/unauthorized-domain': { kind: 'configuration', message: '' },
  'auth/auth-domain-config-required': { kind: 'configuration', message: '' },
  'auth/operation-not-allowed': { kind: 'configuration', message: '' },
  'auth/invalid-api-key': { kind: 'configuration', message: '' },
  'auth/internal-error': { kind: 'configuration', message: '' },

  'auth/invalid-credential': { kind: 'credential', message: INVALID_CREDENTIAL_MESSAGE },
  'auth/invalid-email': { kind: 'credential', message: INVALID_CREDENTIAL_MESSAGE },
  'auth/wrong-password': { kind: 'credential', message: INVALID_CREDENTIAL_MESSAGE },
  'auth/user-not-found': { kind: 'credential', message: INVALID_CREDENTIAL_MESSAGE },
  'auth/missing-password': { kind: 'credential', message: INVALID_CREDENTIAL_MESSAGE },
  'auth/account-exists-with-different-credential': {
    kind: 'credential',
    message: `このメールアドレスは別のログイン方法で登録されています。\n${SIGN_IN_WITH_EMAIL_GUIDANCE}`,
  },
  'auth/user-disabled': {
    kind: 'credential',
    message: 'このアカウントは現在ご利用いただけません。\nサポートまでご連絡ください。',
  },
};

const UNKNOWN_CODE = 'auth/unknown';

// Google / メールどちらの hook からも使うため、認証方式に依存しない文言にする。
const configurationMessage = (code: string) =>
  `ログインできませんでした。\nお手数ですがサポートまでご連絡ください。(エラーコード: ${code})`;

const unknownMessage = (code: string) =>
  `ログインに失敗しました。\n時間をおいて再度お試しください。(エラーコード: ${code})`;

/** FirebaseError かどうかに関わらず、可能な範囲で `auth/...` 形式の code を取り出す */
export const getAuthErrorCode = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const { code } = error as { code: unknown };
    if (typeof code === 'string' && code !== '') {
      return code;
    }
  }
  return UNKNOWN_CODE;
};

/** 利用者自身の操作による中断か (= 画面にエラーを出すべきでないか) */
export const isCancelledByUser = (error: unknown): boolean =>
  toAuthErrorDisplay(error).kind === 'cancelled';

export const toAuthErrorDisplay = (error: unknown): AuthErrorDisplay => {
  const code = getAuthErrorCode(error);
  const mapped = ERROR_MAP[code];

  if (!mapped) {
    return { code, kind: 'unknown', message: unknownMessage(code) };
  }
  if (mapped.kind === 'configuration') {
    return { code, kind: 'configuration', message: configurationMessage(code) };
  }
  return { code, kind: mapped.kind, message: mapped.message };
};

/** hook 側で「未入力」など Firebase 由来でないエラーを同じ型で扱うためのヘルパー */
export const toLocalAuthErrorDisplay = (code: string, message: string): AuthErrorDisplay => ({
  code,
  kind: 'credential',
  message,
});
